import { type Preferences, prefs } from "./PreferenceReference.mjs"

const AbsentStorage: Storage = {
    getItem(): null {
        return null
    },
    setItem(): never {
        throw Error("cannot save to absent storage")
    },
    removeItem(): void { },
    clear(): void { },
    length: 0,
    key: function (): null {
        return null
    }
}

function dumpStorageInto(storage: Storage, map: Map<string, string>): void {
    for (let i = 0; i < storage.length; i += 1) {
        const key = storage.key(i)
        if (!key) {
            break
        }
        const value = storage.getItem(key)
        if (!value) {
            break
        }
        map.set(key, value)
    }
}

function tryGetStorage(key: "localStorage"|"sessionStorage"): Storage|null {
    try {
        return window[key] ?? null
    } catch (ex) {
        return null
    }
}

export interface ExportedPreference {
    preferenceName: string,
    preferenceValue: string,
}

export class PreferencePersistence extends EventTarget {
    readonly local: Storage
    readonly session: Storage

    constructor() {
        super()
        this.local = tryGetStorage("localStorage") ?? AbsentStorage
        this.session = tryGetStorage("sessionStorage") ?? AbsentStorage
        window.addEventListener("storage", (ev) => this.redispatchEvent(ev))
    }

    canAccessStorage(): boolean {
        return this.local !== AbsentStorage || this.session !== AbsentStorage
    }

    private redispatchEvent(ev: StorageEvent): boolean {
        const key = ev.key
        if (!key) {
            return this.dispatchEvent(new StorageEvent("storage", ev))
        }
        const newValue = ev.newValue
        const ourValue = this.getPreference(key)
        if (newValue !== ourValue) {
            return true
        }
        return this.dispatchEvent(new StorageEvent("storage", ev))
    }

    getAllPreferences(): ExportedPreference[] {
        const stored = new Map<string, string>()
        dumpStorageInto(this.local, stored)
        dumpStorageInto(this.session, stored)

        return [...stored].map(([key, value]) => ({preferenceName: key, preferenceValue: value}))
    }

    getPreference(name: string): string|null {
        let value: string|null = null
        try {
            value = this.session.getItem(name)
        } catch (ex) {
            console.error(`getting ${name} from session storage: `, ex)
        }
        if (value === null) {
            try {
                value = this.local.getItem(name)
            } catch (ex) {
                console.error(`getting ${name} from local storage: `, ex)
            }
        }
        return value
    }

    getIntegerPreference(name: string): number|null {
        const result = this.getPreference(name)
        if (result === null) {
            return result
        } else {
            return Number.parseInt(result)
        }
    }

    getFloatPreference(name: string): number|null {
        const result = this.getPreference(name)
        if (result === null) {
            return result
        } else {
            return Number.parseFloat(result)
        }
    }

    getTheme(): string|null {
        return this.getPreference("theme")
    }

    getVariant(variantName: string): string|null {
        return this.getPreference("variant::" + variantName)
    }

    setPreference(name: string, value: string): void {
        const oldValue = this.getPreference(name)
        let locallyStored = null
        try {
            this.local.setItem(name, value)
            locallyStored = true
        } catch (ex) {
            console.error(`setting ${name} to ${value} in local storage: `, ex)
            try {
                this.session.setItem(name, value)
                locallyStored = false
            } catch (ex) {
                console.error(`setting ${name} to ${value} in session storage: `, ex)
            }
        }
        if (locallyStored) {
            try {
                this.session.removeItem(name)
            } catch (ex) {
                console.error(`removing ${name} from session storage when setting it to ${value} in local storage: `, ex)
            }
        }
        const newValue = this.getPreference(name)
        if (locallyStored !== null && oldValue !== newValue) {
            this.dispatchEvent(new StorageEvent("storage", {
                key: name,
                oldValue,
                newValue,
                cancelable: false,
                bubbles: false,
                composed: false,
                url: window.location.href,
                storageArea: null,
            }))
        }
    }

    setNumericPreference(name: string, value: number): void {
        this.setPreference(name, value.toString())
    }

    setTheme(theme: string): void {
        this.setPreference("theme", theme)
    }

    setVariant(variantName: string, value: string): void {
        this.setPreference("variant::" + variantName, value)
    }

    removePreference(name: string): void {
        const oldValue = this.getPreference(name)
        try {
            this.local.removeItem(name)
        } catch (ex) {
            console.error(`removing ${name} from local storage: `, ex)
        }
        try {
            this.session.removeItem(name)
        } catch (ex) {
            console.error(`removing ${name} from session storage: `, ex)
        }
        const newValue = this.getPreference(name)
        if (oldValue !== newValue) {
            this.dispatchEvent(new StorageEvent("storage", {
                key: name,
                oldValue,
                newValue,
                cancelable: false,
                bubbles: false,
                composed: false,
                url: window.location.href,
                storageArea: null,
            }))
        }
    }

    removeTheme(): void {
        this.removePreference("theme")
    }

    removeVariant(variantName: string): void {
        this.removePreference("variant::" + variantName)
    }

    clearSavedPreferences(): void {
        try {
            this.local.clear()
        } catch (ex) {
            console.error("clearing local storage: ", ex)
        }
        try {
            this.session.clear()
        } catch (ex) {
            console.error("clearing session storage: ", ex)
        }
        this.dispatchEvent(new StorageEvent("storage", {
            key: null,
            oldValue: null,
            newValue: null,
            cancelable: false,
            bubbles: false,
            composed: false,
            url: window.location.href,
            storageArea: null,
        }))
    }

    addEventListener(type: "storage", callback: ((ev: StorageEvent) => void) | {handleEvent(ev: StorageEvent): void} | null, options?: boolean | AddEventListenerOptions | undefined): void
    addEventListener(type: string, callback: EventListenerOrEventListenerObject | null, options?: boolean | AddEventListenerOptions | undefined): void {
        return super.addEventListener(type, callback, options)
    }
}

export const persistence = new PreferencePersistence()

export function setPagePersistenceLevel() {
    document.documentElement.dataset.persistence = persistence.canAccessStorage() ? "on" : "jsOnly"
}

export function persistPreferences(prefs: Preferences, persistence: PreferencePersistence) {
    {
        for (const [key, pref] of prefs.items) {
            switch (pref.type) {
                case "number":
                    const numberValue = persistence.getFloatPreference(key)
                    if (numberValue === null || Number.isNaN(numberValue)) {
                        continue
                    }
                    pref.element.valueAsNumber = numberValue
                    break
                case "enum":
                    const enumValue = persistence.getPreference(key)
                    if (enumValue === null) {
                        continue
                    }
                    const {element, offValue} = pref.values.get(enumValue) ?? {element: null, offValue: false}
                    if (!element) {
                        console.error(`unknown value for ${key} -> ${enumValue}`)
                        continue
                    }
                    element.checked = !offValue
                    break
            }
        }
    }
    persistence.addEventListener("storage", (ev) => {
        const key = ev.key
        if (key === null) {
            for (const [key, pref] of prefs.items) {
                switch (pref.type) {
                    case "number":
                        const numberValue = pref.defaultValue
                        pref.element.valueAsNumber = numberValue
                        break
                    case "enum":
                        const enumValue = pref.defaultValue ?? [...pref.values.keys()][0]
                        const {element, offValue} = pref.values.get(enumValue) ?? {element: null, offValue: false}
                        if (!element) {
                            console.error(`unknown value for ${key} -> ${enumValue}`)
                            continue
                        }
                        element.checked = !offValue
                        break
                }
            }
            return
        } else {
            const pref = prefs.items.get(key)
            if (!pref) {
                return
            }
            switch (pref.type) {
                case "number":
                    const newNumberValue = ev.newValue === null ? null : parseFloat(ev.newValue)
                    const numberValue = newNumberValue ?? pref.defaultValue
                    pref.element.valueAsNumber = numberValue
                    break
                case "enum":
                    const enumValue = ev.newValue ?? pref.defaultValue ?? [...pref.values.keys()][0]
                    const {element, offValue} = pref.values.get(enumValue) ?? {element: null, offValue: false}
                    if (!element) {
                        console.error(`unknown value for ${key} -> ${enumValue}`)
                        return
                    }
                    element.checked = !offValue
                    break
            }
        }
    })
    document.addEventListener("change", (ev: Event) => {
        const target = ev.target
        if (!(target instanceof HTMLInputElement && target.classList.contains("persistedPreference")) || !target.checkValidity()) {
            return
        }
        const name = target.name
        const value = target.value
        const offValue = target.dataset.offValue
        if (target.type === "range" || target.type === "number" || target.checked) {
            persistence.setPreference(name, value)
        } else if (offValue) {
            persistence.setPreference(name, offValue)
        }
    })
}

export function persistPagePreferences(): void {
    persistPreferences(prefs, persistence)
}